import React from "react";
import { Link } from "react-router-dom";
import BrassRule from "@/components/BrassRule";
import LogoLockup from "@/components/LogoLockup";
import { useLang } from "@/lib/i18n";
import { useSeo } from "@/lib/useSeo";

/**
 * The Collection — editorial "about this collection" page.
 * Five sections: hero, premise, criteria, geography, studio + CTA.
 */
export default function TheCollection() {
  const { t, lang } = useLang();
  const a = t.about;

  useSeo({
    title:
      lang === "es"
        ? "Sobre la Colección — Viaja de Verdad"
        : "About the Collection — Viaja de Verdad",
    description: a.hero_sub,
    image: "/static/experiences/donana-horseback/hero.png",
    locale: lang === "es" ? "es_ES" : "en_US",
    type: "article",
  });

  return (
    <div data-testid="the-collection-page" className="bg-ivory">
      {/* HERO */}
      <section className="relative h-[88svh] min-h-[560px] w-full overflow-hidden bg-charcoal text-ivory">
        <img
          src="/static/experiences/donana-horseback/hero.png"
          alt=""
          className="absolute inset-0 w-full h-full object-cover page-hero-kb opacity-90"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(15,15,15,0.45) 0%, rgba(15,15,15,0.18) 35%, rgba(15,15,15,0.10) 60%, rgba(15,15,15,0.75) 100%)",
          }}
          aria-hidden
        />
        <div className="relative z-10 h-full mx-auto max-w-7xl px-6 md:px-10 flex flex-col justify-end pb-28 md:pb-32 hero-legible">
          <p
            className="eyebrow text-brass mb-6 animate-rise-in"
            style={{ animationDelay: "180ms", opacity: 0 }}
            data-testid="about-eyebrow"
          >
            {a.hero_eyebrow}
          </p>
          <h1
            className="font-display text-ivory leading-[1.04] tracking-tight max-w-3xl text-balance animate-rise-in"
            style={{ fontSize: "clamp(2.5rem, 6vw, 5.25rem)", animationDelay: "320ms", opacity: 0 }}
            data-testid="about-title"
          >
            {a.hero_title}{" "}
            <span className="italic-quote font-normal" style={{ fontWeight: 400 }}>
              {a.hero_italic}
            </span>
          </h1>
          <p
            className="italic-quote text-ivory/85 mt-6 max-w-2xl animate-rise-in text-balance"
            style={{ fontSize: "clamp(1.1rem, 1.5vw, 1.4rem)", animationDelay: "480ms", opacity: 0 }}
          >
            {a.hero_sub}
          </p>
        </div>
      </section>

      {/* THE PREMISE */}
      <section className="py-28 md:py-40 bg-ivory">
        <div className="mx-auto max-w-5xl px-6 md:px-10">
          <p className="eyebrow text-emerald" data-testid="about-premise-eyebrow">{a.premise_eyebrow}</p>
          <h2
            className="font-display text-charcoal mt-6 leading-[1.08] text-balance"
            style={{ fontSize: "clamp(2rem, 4vw, 3.25rem)" }}
          >
            {a.premise_title}
          </h2>
          <BrassRule className="mt-8" width={60} />
          <div className="mt-10 grid md:grid-cols-2 gap-10 md:gap-14 text-charcoal/85">
            <p style={{ fontSize: "clamp(1.05rem, 1.2vw, 1.2rem)", lineHeight: 1.7 }}>{a.premise_p1}</p>
            <p style={{ fontSize: "clamp(1.05rem, 1.2vw, 1.2rem)", lineHeight: 1.7 }}>{a.premise_p2}</p>
          </div>
        </div>
      </section>

      {/* THE CRITERIA */}
      <section className="py-28 md:py-36 bg-pearl border-t border-mist">
        <div className="mx-auto max-w-6xl px-6 md:px-10">
          <p className="eyebrow text-emerald" data-testid="about-criteria-eyebrow">{a.criteria_eyebrow}</p>
          <h2
            className="font-display text-charcoal mt-6 leading-[1.08] text-balance max-w-3xl"
            style={{ fontSize: "clamp(1.85rem, 3.6vw, 3rem)" }}
          >
            {a.criteria_title}
          </h2>
          <BrassRule className="mt-8" width={60} />

          <ol className="mt-14 grid md:grid-cols-2 gap-x-12 gap-y-14">
            {a.criteria_items.map((item, idx) => (
              <li key={item.k} className="relative pl-10" data-testid={`criteria-${idx + 1}`}>
                <span
                  className="absolute left-0 top-1 eyebrow text-brass"
                  style={{ fontSize: "11px" }}
                  aria-hidden
                >
                  0{idx + 1}
                </span>
                <h3 className="font-display text-charcoal leading-snug" style={{ fontSize: "clamp(1.3rem, 1.6vw, 1.5rem)" }}>
                  {item.k}
                </h3>
                <span className="block h-px bg-brass mt-3 w-10" aria-hidden />
                <p
                  className="mt-4 text-charcoal/80"
                  style={{ fontSize: "clamp(1rem, 1.1vw, 1.1rem)", lineHeight: 1.65 }}
                >
                  {item.v}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* THE GEOGRAPHY */}
      <section className="relative py-28 md:py-40 bg-emerald text-ivory overflow-hidden">
        <img
          src="/static/experiences/arrabida-ebike-wine/hero.png"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-30 page-hero-kb"
        />
        <div className="absolute inset-0 bg-emerald/85" aria-hidden />
        <div className="relative z-10 mx-auto max-w-4xl px-6 md:px-10">
          <p className="eyebrow text-brass" data-testid="about-geography-eyebrow">{a.geography_eyebrow}</p>
          <h2
            className="font-display mt-6 leading-[1.08] text-balance"
            style={{ fontSize: "clamp(2rem, 4vw, 3.25rem)" }}
          >
            {a.geography_title}
          </h2>
          <BrassRule className="mt-8 bg-brass" width={60} />
          <p
            className="italic-quote text-ivory/90 mt-10 max-w-2xl text-balance"
            style={{ fontSize: "clamp(1.15rem, 1.5vw, 1.4rem)", lineHeight: 1.6 }}
          >
            {a.geography_p}
          </p>

          <ul className="mt-12 grid grid-cols-3 gap-4 max-w-xl">
            {[
              { code: "spain", to: "/spain" },
              { code: "italy", to: "/italy" },
              { code: "portugal", to: "/portugal" },
            ].map(({ code, to }) => (
              <li key={code}>
                <Link
                  to={to}
                  className="block text-center eyebrow text-ivory hover:text-brass transition-colors"
                  data-testid={`about-country-link-${code}`}
                >
                  {t.nav[code]}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* THE STUDIO */}
      <section className="py-28 md:py-40 bg-ivory">
        <div className="mx-auto max-w-5xl px-6 md:px-10 grid md:grid-cols-12 gap-10 md:gap-16 items-start">
          <div className="md:col-span-5">
            <p className="eyebrow text-emerald" data-testid="about-studio-eyebrow">{a.studio_eyebrow}</p>
            <h2
              className="font-display text-charcoal mt-6 leading-[1.1] text-balance"
              style={{ fontSize: "clamp(1.85rem, 3.4vw, 2.85rem)" }}
            >
              {a.studio_title}
            </h2>
            <BrassRule className="mt-8" width={60} />
          </div>
          <div className="md:col-span-7 space-y-7 text-charcoal/85">
            <p style={{ fontSize: "clamp(1.05rem, 1.2vw, 1.2rem)", lineHeight: 1.75 }}>{a.studio_p1}</p>
            <p
              className="italic-quote text-charcoal/80"
              style={{ fontSize: "clamp(1.15rem, 1.35vw, 1.35rem)", lineHeight: 1.65 }}
            >
              {a.studio_p2}
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 md:py-32 bg-charcoal text-ivory overflow-hidden">
        <img
          src="/static/experiences/agrippa-baths-candlelight/hero.png"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-40 page-hero-kb"
        />
        <div className="absolute inset-0 bg-charcoal/70" aria-hidden />
        <div className="relative z-10 mx-auto max-w-4xl px-6 md:px-10 text-center">
          <div className="mb-12 flex justify-center" data-testid="collection-tagline-wrap">
            <LogoLockup variant="full" size="lg" tone="dark" data-testid="collection-tagline" />
          </div>
          <p className="eyebrow text-brass">{a.cta_eyebrow}</p>
          <h2
            className="font-display text-ivory mt-6 leading-[1.05] text-balance"
            style={{ fontSize: "clamp(2rem, 4.5vw, 3.5rem)" }}
          >
            {a.cta_main}{" "}
            <span className="italic-quote font-normal" style={{ fontWeight: 400 }}>{a.cta_italic}</span>
          </h2>
          <p
            className="italic-quote text-ivory/85 mt-6 max-w-2xl mx-auto text-balance"
            style={{ fontSize: "clamp(1.05rem, 1.3vw, 1.25rem)" }}
          >
            {a.cta_sub}
          </p>
          <div className="mt-10">
            <Link
              to="/experiences"
              className="btn-brass text-ivory inline-flex"
              data-testid="about-cta-button"
            >
              {a.cta_button}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
